// @ts-strict-ignore

import { removeTimeZone } from 'utils/DateUtils';

import {
  OralAnswerStructure,
  OralReportRegimenMeta,
  OralReportResponse,
  ReportQuestionAnswer
} from 'models/OralReport/OralReport.shared';

export enum GeneralAnswer {
  Yes = 'yes',
  No = 'no'
}

export enum MissedDosesAnswer {
  None = 'none',
  OneOrTwo = 'oneOrTwo',
  ThreeOrFour = 'threeOrFour',
  MoreThanFour = 'moreThanFour'
}

export enum RemainingDosesAnswer {
  NoDoses = 'noDoses',
  LowDoses = 'lowDoses',
  WeeksDoses = 'weeksDoses',
  ManyDoses = 'manyDoses'
}

export enum ActiveMedicationAnswer {
  NotReceived = 'notReceived'
}

export type OralAnswerValueV3 =
  | boolean
  | number
  | string
  | Date
  | GeneralAnswer
  | MissedDosesAnswer
  | RemainingDosesAnswer
  | ActiveMedicationAnswer;

export enum OralQuestionTypeV3 {
  ActiveMedication = 'activeMedication',
  StartOfCycle = 'startOfCycle',
  RegimenAdherence = 'regimenAdherence',
  MissedDoses = 'missedDoses',
  DosesRemaining = 'dosesRemainingV3',
  RefillRequest = 'refillRequest'
}

export class OralReportV3 implements OralReportRegimenMeta {
  startOfCycleDate: Date;
  regimen: string;
  isCustom: false;
  [OralQuestionTypeV3.ActiveMedication]: ReportQuestionAnswer<
    boolean | ActiveMedicationAnswer,
    OralQuestionTypeV3
  >;
  [OralQuestionTypeV3.StartOfCycle]?: ReportQuestionAnswer<Date, OralQuestionTypeV3>;
  [OralQuestionTypeV3.RegimenAdherence]?: ReportQuestionAnswer<boolean, OralQuestionTypeV3>;
  [OralQuestionTypeV3.MissedDoses]?: ReportQuestionAnswer<MissedDosesAnswer, OralQuestionTypeV3>;
  [OralQuestionTypeV3.DosesRemaining]?: ReportQuestionAnswer<
    RemainingDosesAnswer,
    OralQuestionTypeV3
  >;
  [OralQuestionTypeV3.RefillRequest]?: ReportQuestionAnswer<boolean, OralQuestionTypeV3>;
  previousReportDate?: Date;

  constructor(
    oralReportUnparsedResponse: OralReportResponse<OralAnswerValueV3, OralQuestionTypeV3>
  ) {
    Object.assign(this, oralReportUnparsedResponse);
    this.startOfCycleDate = oralReportUnparsedResponse?.startOfCycle
      ? this.normalizeCycleDate(oralReportUnparsedResponse.startOfCycle)
      : null;
    this.previousReportDate = oralReportUnparsedResponse?.previousReportDate
      ? this.normalizeCycleDate(oralReportUnparsedResponse.previousReportDate)
      : null;

    oralReportUnparsedResponse.reportItems.forEach((answer) => {
      switch (answer.questionType) {
        case OralQuestionTypeV3.StartOfCycle:
          if (answer.value) {
            answer.value = this.normalizeCycleDate(answer.value as string);
          }

          this[OralQuestionTypeV3.StartOfCycle] = new ReportQuestionAnswer<
            Date,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<Date, OralQuestionTypeV3>);
          break;

        case OralQuestionTypeV3.ActiveMedication:
          this[OralQuestionTypeV3.ActiveMedication] = new ReportQuestionAnswer<
            boolean | ActiveMedicationAnswer,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<boolean | ActiveMedicationAnswer, OralQuestionTypeV3>, {
            value: true
          });
          break;

        case OralQuestionTypeV3.RegimenAdherence:
          this[OralQuestionTypeV3.RegimenAdherence] = new ReportQuestionAnswer<
            boolean,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<boolean, OralQuestionTypeV3>, {
            value: false
          });
          break;

        case OralQuestionTypeV3.MissedDoses:
          this[OralQuestionTypeV3.MissedDoses] = new ReportQuestionAnswer<
            MissedDosesAnswer,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<MissedDosesAnswer, OralQuestionTypeV3>, {
            value: MissedDosesAnswer.None
          });
          break;

        case OralQuestionTypeV3.DosesRemaining:
          this[OralQuestionTypeV3.DosesRemaining] = new ReportQuestionAnswer<
            RemainingDosesAnswer,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<RemainingDosesAnswer, OralQuestionTypeV3>, {
            value: [RemainingDosesAnswer.WeeksDoses, RemainingDosesAnswer.ManyDoses]
          });
          break;

        case OralQuestionTypeV3.RefillRequest:
          this[OralQuestionTypeV3.RefillRequest] = new ReportQuestionAnswer<
            boolean,
            OralQuestionTypeV3
          >(answer as OralAnswerStructure<boolean, OralQuestionTypeV3>, {
            value: false,
            customMessageExistenceValue: false
          });
          break;
      }
    });
  }

  private normalizeCycleDate(date: string): Date {
    // server needs to turn this into date string and not datetime string to avoid date issues like with DOB
    return new Date(removeTimeZone(date));
  }

  get newCycleDate() {
    // if patient did not provide first pill date - use startOfCycle meta (can be update by a nurse)
    return this[OralQuestionTypeV3.StartOfCycle]?.isAnswered
      ? this[OralQuestionTypeV3.StartOfCycle].value
      : this.startOfCycleDate;
  }

  set cycle(date: Date) {
    this.startOfCycleDate = date;
  }
}
